// SPDX-License-Identifier: LicenseRef-Proprietary
// Copyright (c) 2025 Mann Contractual Management Ltd

/**
 * GoatCounter Client-Side Integration
 * Handles localStorage skip functionality and URL cleanup
 */

// GoatCounter configuration
const GOATCOUNTER_CONFIG = {
  skipKey: "skipgc",
  allowLocal: process.env.GATSBY_ALLOW_LOCAL_ANALYTICS === "true",
}

// Check if tracking should be skipped
function shouldSkipTracking() {
  if (typeof window === "undefined" || !window.localStorage) {
    return false
  }

  // Check if user has opted out
  return window.localStorage.getItem(GOATCOUNTER_CONFIG.skipKey) === "t"
}

// Handle URL cleanup for referrer tracking
function cleanupUrl() {
  if (typeof window === "undefined" || !window.history) {
    return
  }

  const url = new URL(window.location.href)

  // Remove referrer and utm parameters
  const paramsToRemove = [
    "ref",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
  ]

  let hasRemovedParams = false
  paramsToRemove.forEach((param) => {
    if (url.searchParams.has(param)) {
      url.searchParams.delete(param)
      hasRemovedParams = true
    }
  })

  // Update URL without causing page reload
  if (hasRemovedParams) {
    window.history.replaceState({}, "", url.toString())
  }
}

// Initialize GoatCounter with performance optimizations
function initGoatCounter() {
  // Skip if user has opted out
  if (shouldSkipTracking()) {
    window.goatcounter = { no_onload: true }
    return
  }

  // Handle hash-based opt-out
  if (typeof window !== "undefined" && window.location.hash === "#skipgc") {
    window.localStorage.setItem(GOATCOUNTER_CONFIG.skipKey, "t")
    window.goatcounter = { no_onload: true }

    // Show user feedback
    if (window.alert) {
      window.alert(
        "GoatCounter tracking disabled. Visit /#skipgc again to re-enable."
      )
    }

    // Clean URL
    const url = new URL(window.location.href)
    url.hash = ""
    window.history.replaceState({}, "", url.toString())

    return
  }

  // Clean URL for referrer tracking
  cleanupUrl()

  // Performance: Only load if GoatCounter script is present
  if (typeof window.goatcounter === "undefined") {
    // Script will be loaded via SSR, just configure it
    window.goatcounter = window.goatcounter || {}
  }
}

// Initialize on page load
if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initGoatCounter)
  } else {
    initGoatCounter()
  }
}

// Export for potential manual usage
if (typeof module !== "undefined" && module.exports) {
  module.exports = { shouldSkipTracking, cleanupUrl, initGoatCounter }
}
